/**
 * The part of a video its subtitle track LIST is in, read without reading the video.
 *
 * Listing tracks is what every video drop starts with, and most videos have none: a phone
 * recording, a screen capture. Reading 2 GB to say so would be absurd, so the page reads only:
 *
 *   MKV/WebM  the first megabyte, then more if the track list runs past it. The track list comes
 *             before the clusters in every muxer's output; a file with large attachments
 *             before it is the only reason to read further.
 *   MP4/MOV   the top-level boxes' headers, 16 bytes each, to find `moov` wherever it sits
 *             (an unoptimised file keeps it at the END), then `ftyp` and `moov` themselves, joined
 *             into a small buffer the demuxer reads like a whole file. Sample data is never read.
 *   other     the first megabyte, which is enough for the demuxer to name what the file is and
 *             say what to do instead (AVI, TS).
 *
 * Extracting a TEXT track still needs the whole file (an MKV's subtitle blocks are spread through
 * all of it); that read happens only once a track is chosen.
 */

const MIB = 1024 * 1024;
/** The first read of an MKV head. */
export const HEAD_FIRST = MIB;
/** Past this, a track list that has not appeared is read with the whole file. */
export const HEAD_MAX = 64 * MIB;
/** Top-level boxes walked looking for `moov` before giving up on the walk. */
const MAX_BOXES = 256;

export type VideoHead =
  /** Bytes to probe. `partial`: they are the start of a larger file. */
  { readonly bytes: Uint8Array; readonly partial: boolean };

async function slice(file: Blob, from: number, to: number): Promise<Uint8Array> {
  return new Uint8Array(await file.slice(from, Math.min(to, file.size)).arrayBuffer());
}

const ascii4 = (b: Uint8Array, at: number): string =>
  String.fromCharCode(b[at] ?? 0, b[at + 1] ?? 0, b[at + 2] ?? 0, b[at + 3] ?? 0);

const be32 = (b: Uint8Array, at: number): number =>
  (((b[at] ?? 0) << 24) | ((b[at + 1] ?? 0) << 16) | ((b[at + 2] ?? 0) << 8) | (b[at + 3] ?? 0)) >>>
  0;

export type ContainerFamily = 'matroska' | 'mp4' | 'other';

/** Which family the first bytes say this is. Same tests as the demuxer's `sniffContainer`. */
export function familyOf(first: Uint8Array): ContainerFamily {
  if (be32(first, 0) === 0x1a45dfa3) return 'matroska';
  if (ascii4(first, 4) === 'ftyp') return 'mp4';
  return 'other';
}

/** One top-level box: where it starts and how long it is (to the end of the file for size 0). */
interface TopBox {
  readonly type: string;
  readonly at: number;
  readonly size: number;
}

/** The top-level boxes of an MP4, from their headers alone. Stops at `moov` or at damage. */
export async function topLevelBoxes(file: Blob): Promise<TopBox[]> {
  const out: TopBox[] = [];
  let at = 0;
  while (at + 8 <= file.size && out.length < MAX_BOXES) {
    const head = await slice(file, at, at + 16);
    const size32 = be32(head, 0);
    const type = ascii4(head, 4);
    let size: number;
    if (size32 === 1) {
      // 64-bit size: high word, low word. Files over 4 GiB state their mdat this way.
      size = be32(head, 8) * 2 ** 32 + be32(head, 12);
    } else if (size32 === 0) {
      size = file.size - at;
    } else {
      size = size32;
    }
    if (size < 8) break; // damage: a box cannot be smaller than its header
    out.push({ type, at, size });
    if (type === 'moov') break;
    at += size;
  }
  return out;
}

/**
 * The bytes the track list is in. For an MP4 that is `ftyp` + `moov`, joined: the demuxer finds
 * `ftyp` at offset 4 and `moov` after it, and listing tracks reads nothing else. A `moov` that
 * cannot be found that way (a damaged walk) falls back to the head.
 */
export async function videoHead(file: Blob, family: ContainerFamily): Promise<VideoHead> {
  if (family === 'mp4') {
    const boxes = await topLevelBoxes(file);
    const ftyp = boxes.find((b) => b.type === 'ftyp');
    const moov = boxes.find((b) => b.type === 'moov');
    if (ftyp && moov && ftyp.at === 0 && moov.size <= HEAD_MAX) {
      const head = await slice(file, ftyp.at, ftyp.at + ftyp.size);
      const index = await slice(file, moov.at, moov.at + moov.size);
      const joined = new Uint8Array(head.length + index.length);
      joined.set(head, 0);
      joined.set(index, head.length);
      return { bytes: joined, partial: false };
    }
  }
  const bytes = await slice(file, 0, HEAD_FIRST);
  return { bytes, partial: file.size > bytes.length };
}

/** The next, larger head of an MKV whose track list was not in the last one; null: read it all. */
export async function longerHead(file: Blob, had: number): Promise<VideoHead | null> {
  const next = had * 4;
  if (next > HEAD_MAX || had >= file.size) return null;
  const bytes = await slice(file, 0, next);
  return { bytes, partial: file.size > bytes.length };
}
